// pages/DriverProfilePage.jsx
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getTeamImage, useTeamImageFallback } from "../utils/teamImages";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Link,
  Paper,
  Skeleton,
  Stack,
  SvgIcon,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PageShell from "../components/PageShell";
import { ACC_FILES, AC_FILES, AC_EVO_FILES } from "../results/files.config";
import { aceSessionRow } from "../utils/aceResults";
import { useNavigate } from "react-router-dom";
import { getLicenseLabel, getLicenseColor, computeLicensePoints, LICENSE_TIERS } from "../utils/drivers.util";

function SteamIcon(props) {
  //public/steam.png
  return (
    <img
      src="/steam.png"
      alt="Steam"
      width={16}
      height={16}
      style={{ borderRadius: "2px" }}
      {...props}
    />
  );
}

/* =========================
   SAME INPUTS / HELPERS
   ========================= */

const msToLap = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0 || ms === 2147483647 || ms === 999999999) return "—";
  const total = Math.floor(ms);
  const m = Math.floor(total / 60000);
  const s = Math.floor((total % 60000) / 1000);
  const rem = total % 1000;
  return `${m}:${s.toString().padStart(2, "0")}.${rem.toString().padStart(3, "0")}`;
};

/**
 * ===============================
 * Local cache for fetched JSON
 * ===============================
 * Cache Promise<json|null> per URL so the same file is never fetched twice
 * (and concurrent callers share the same in-flight request).
 */
const __jsonPromiseCache = new Map(); // url -> Promise<any|null>
export function clearDriverProfileFetchCache() {
  __jsonPromiseCache.clear();
}

async function fetchJSONUncached(url) {
  try {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) {
      console.warn("File not found:", url, res.status);
      return null;
    }

    let buf = await res.arrayBuffer();
    let u8 = new Uint8Array(buf);

    const isGzip = u8.length > 2 && u8[0] === 0x1f && u8[1] === 0x8b;
    if (isGzip) {
      try {
        if (typeof DecompressionStream === "function") {
          const ds = new DecompressionStream("gzip");
          const stream = new Response(buf).body.pipeThrough(ds);
          buf = await new Response(stream).arrayBuffer();
          u8 = new Uint8Array(buf);
        }
      } catch (e) {
        console.error("Gzip decode failed:", url, e);
        return null;
      }
    }

    const tryDecode = (enc) => {
      try {
        return JSON.parse(new TextDecoder(enc, { fatal: false }).decode(u8));
      } catch {
        return null;
      }
    };

    return tryDecode("utf-8") || tryDecode("utf-16le") || tryDecode("utf-16be");
  } catch (e) {
    console.error("fetchJSON failed:", url, e);
    return null;
  }
}

async function fetchJSON(url) {
  const key = String(url);

  if (__jsonPromiseCache.has(key)) {
    return __jsonPromiseCache.get(key);
  }

  const p = (async () => {
    const json = await fetchJSONUncached(key);
    return json;
  })();

  __jsonPromiseCache.set(key, p);

  p.catch(() => {
    __jsonPromiseCache.delete(key);
  });

  return p;
}

const labelSession = (raw) => {
  const v = String(raw || "").toUpperCase();
  if (["FP", "P", "PRACTICE"].includes(v)) return "Practice";
  if (["Q", "QUALIFY", "QUALI", "QUALIFYING"].includes(v)) return "Qualifying";
  if (["R", "RACE"].includes(v)) return "Race";
  return raw || "—";
};

const cleanId = (v) => {
  if (!v) return "";
  const str = String(v).trim();
  return str.replace(/^steam:/i, "").replace(/^S/i, "");
};
const idsEqual = (a, b) => cleanId(a) === cleanId(b);

/* =========================
   PARSERS (extended to include position for Race)
   ========================= */

function parseACC(json, driverId, fileName) {
  const lines = json?.sessionResult?.leaderBoardLines ?? [];
  const driverIdClean = cleanId(driverId);

  const idx = lines.findIndex((l) => {
    const cur = l?.currentDriver?.playerId;
    const d0 = l?.car?.drivers?.[0]?.playerId;
    return idsEqual(cur, driverIdClean) || idsEqual(d0, driverIdClean);
  });
  if (idx < 0) return null;

  const hit = lines[idx];
  const playerId = hit?.currentDriver?.playerId || hit?.car?.drivers?.[0]?.playerId;
  const normalizedId = playerId?.startsWith("S") ? playerId : `S${cleanId(playerId)}`;

  const best = hit?.timing?.bestLap ?? null;
  const lapCount = hit?.timing?.lapCount ?? null;

  const sessionName = labelSession(json?.sessionType ?? json?.Type);

  let track = json?.trackName ?? json?.TrackName ?? json?.metaData ?? "—";
  track = track.replace("_", " ").trim();
  //Uppercase each work
  let words = track.split(" ");
  words = words.map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  track = words.join(" ");

  return {
    fileName,
    sim: "ACC",
    playerId: normalizedId,
    session: sessionName,
    track: track,
    best,
    laps: Number.isFinite(lapCount) ? lapCount : null,
    position: sessionName === "Race" ? idx + 1 : null,
    date: json?.date ?? null,
  };
}

function parseAC(json, driverId, fileName) {
  const guid = cleanId(driverId);
  const laps = Array.isArray(json?.Laps) ? json.Laps : [];
  const results = Array.isArray(json?.Result) ? json.Result : [];
  const cars = Array.isArray(json?.Cars) ? json.Cars : [];

  const byLap = laps.filter((l) => cleanId(l?.DriverGuid ?? l?.Driver?.Guid) === guid);
  const inResultsIdx = results.findIndex((r) => cleanId(r?.DriverGuid) === guid);
  const inResults = inResultsIdx >= 0;
  const inCars = cars.some((c) => cleanId(c?.Driver?.Guid) === guid);

  if (!byLap.length && !inResults && !inCars) return null;

  let best = null;
  for (const l of byLap) {
    const t = l?.LapTime;
    if (Number.isFinite(t) && t > 0 && t !== 999999999) best = best === null ? t : Math.min(best, t);
  }

  const sessionName = labelSession(json?.Type);
  
  let track = json?.trackName ?? json?.TrackName ?? json?.metaData ?? "—";
  track = track.replace("_", " ").trim();
  //Uppercase each work
  let words = track.split(" ");
  words = words.map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  track = words.join(" ");
  return {
    fileName,
    sim: "AC",
    session: sessionName,
    track: track,
    best,
    laps: byLap.length || null,
    position: sessionName === "Race" && inResults ? inResultsIdx + 1 : null,
  };
}

/* =========================
   NEW UI HELPERS (kept & extended)
   ========================= */

const SimChip = ({ sim }) => {
  const colorMap = {
    AC: { bg: "rgba(244, 67, 54, 0.12)", fg: "#ef5350", label: "AC" },
    ACC: { bg: "rgba(255, 193, 7, 0.12)", fg: "#fbc02d", label: "ACC" },
    AC_EVO: { bg: "rgba(0, 188, 212, 0.12)", fg: "#26c6da", label: "ACE" },
  };
  const c = colorMap[sim] || { bg: "action.hover", fg: "text.primary", label: sim || "—" };
  return (
    <Chip
      label={c.label}
      size="small"
      sx={{
        bgcolor: c.bg,
        color: c.fg,
        fontWeight: 700,
        borderRadius: 1.5,
      }}
    />
  );
};

const SessionChip = ({ session }) => {
  const v = (session || "").toLowerCase();
  const palette = {
    practice: { bg: "rgba(33,150,243,.12)", fg: "#2196f3" },
    qualifying: { bg: "rgba(156,39,176,.12)", fg: "#9c27b0" },
    race: { bg: "rgba(76,175,80,.12)", fg: "#4caf50" },
  };
  const c =
    v.includes("race") ? palette.race : v.includes("qual") ? palette.qualifying : palette.practice;
  return (
    <Chip
      label={session || "—"}
      size="small"
      sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 700, borderRadius: 1.5 }}
    />
  );
};

const StatBlock = ({ label, value, sub }) => (
  <Box sx={{width: "100%", p: 2, borderRadius: 3, textAlign: "center", margin: "auto"}}>
    <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
      {sub ? ` · ${sub}` : ""}
    </Typography>
  </Box>
);

const PBCard = ({ title, pb }) => (
  <Paper sx={{ p: 2, borderRadius: 3 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
      {title}
    </Typography>
    {pb ? (
      <Stack spacing={0.25}>
        <Typography variant="h5" sx={{ fontWeight: 900 }}>
          {msToLap(pb.time)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {pb.meta?.track || "—"} • {pb.meta?.session || "—"} • {pb.meta?.sim || "—"}
        </Typography>
      </Stack>
    ) : (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )}
  </Paper>
);

/* =========================
   LICENSE & ACHIEVEMENTS (extended)
   ========================= */

function scoreToLicense(X) {
  const tier =
    LICENSE_TIERS.find((t) => X >= t.min && X <= t.max) || LICENSE_TIERS[LICENSE_TIERS.length - 1];
  const nextIdx = LICENSE_TIERS.indexOf(tier) + 1;
  const next = LICENSE_TIERS[nextIdx] || null;
  let pct = 100;
  if (tier.max !== Infinity) {
    const range = tier.max - tier.min + 1;
    pct = Math.max(0, Math.min(100, Math.round(((X - tier.min) / range) * 100)));
  }
  return { tier, next, progressPct: pct };
}

function computeRaceStatsFromRows(rows) {
  const raceRows = rows.filter((r) => (r.session || "").toLowerCase() === "race");
  const ordered = [...raceRows].reverse();

  const finishes = raceRows.length;

  const podiums = raceRows.reduce(
    (acc, r) => (Number.isFinite(r?.position) && r.position <= 3 ? acc + 1 : acc),
    0
  );
  const wins = raceRows.reduce((acc, r) => (r.position === 1 ? acc + 1 : acc), 0);

  const positions = ordered.map((r) => r?.position).filter((v) => Number.isFinite(v));
  const bestPos = positions.length ? Math.min(...positions) : null;
  const worstPos = positions.length ? Math.max(...positions) : null;
  const avgPos = positions.length ? positions.reduce((a, b) => a + b, 0) / positions.length : null;

  const top15s = ordered.reduce(
    (a, r) => (Number.isFinite(r?.position) && r.position <= 15 ? a + 1 : a),
    0
  );
  const top10s = ordered.reduce(
    (a, r) => (Number.isFinite(r?.position) && r.position <= 10 ? a + 1 : a),
    0
  );
  const top5s = ordered.reduce(
    (a, r) => (Number.isFinite(r?.position) && r.position <= 5 ? a + 1 : a),
    0
  );
  const top2s = ordered.reduce(
    (a, r) => (Number.isFinite(r?.position) && r.position <= 2 ? a + 1 : a),
    0
  );

  const totalRaceLaps = ordered.reduce((a, r) => a + (Number.isFinite(r?.laps) ? r.laps : 0), 0);

  const uniqueTracksSet = new Set(ordered.map((r) => (r?.track || "").trim()).filter(Boolean));
  const uniqueTracks = uniqueTracksSet.size;

  const racesAC = ordered.reduce((a, r) => (r?.sim === "AC" ? a + 1 : a), 0);
  const racesACC = ordered.reduce((a, r) => (r?.sim === "ACC" ? a + 1 : a), 0);
  const racesACE = ordered.reduce((a, r) => (r?.sim === "AC_EVO" ? a + 1 : a), 0);

  const perTrackCounts = {};
  for (const r of ordered) {
    const t = (r?.track || "").trim();
    if (!t) continue;
    perTrackCounts[t] = (perTrackCounts[t] || 0) + 1;
  }
  const mostRacesOnOneTrack = Object.values(perTrackCounts).reduce((m, v) => Math.max(m, v), 0);

  const bestStreakWhere = (fn) => {
    let cur = 0;
    let best = 0;
    for (const r of ordered) {
      if (fn(r)) {
        cur += 1;
        best = Math.max(best, cur);
      } else {
        cur = 0;
      }
    }
    return best;
  };

  const bestTop10Streak = bestStreakWhere(
    (r) => Number.isFinite(r?.position) && r.position <= 10
  );
  const bestTop5Streak = bestStreakWhere(
    (r) => Number.isFinite(r?.position) && r.position <= 5
  );
  const bestFinishStreak = ordered.length;

  const X = computeLicensePoints({ finishes, podiums, wins });

  return {
    finishes,
    podiums,
    wins,
    bestPos,
    worstPos,
    avgPos,
    X,
    top15s,
    top10s,
    top5s,
    top2s,
    totalRaceLaps,
    uniqueTracks,
    racesAC,
    racesACC,
    racesACE,
    mostRacesOnOneTrack,
    bestTop10Streak,
    bestTop5Streak,
    bestFinishStreak,
  };
}

function buildAchievements({
  finishes, podiums, wins, bestPos, X,

  top15s, top10s, top5s, top2s,
  totalRaceLaps,
  uniqueTracks,
  racesAC, racesACC, racesACE,
  mostRacesOnOneTrack,
  bestTop10Streak, bestTop5Streak, bestFinishStreak,
  worstPos, avgPos,
}) {
  const p = (have, need) =>
    Number.isFinite(need) ? { have: Math.min(have || 0, need), need } : undefined;

  return [
    // =====================
    // ENDURANCE: TOTAL RACE LAPS (15)
    // =====================
    { key: "laps_10", label: "Warm Up", desc: "Complete 10 race laps", unlocked: totalRaceLaps >= 10, progress: p(totalRaceLaps, 10) },
    { key: "laps_25", label: "Seat Time", desc: "Complete 25 race laps", unlocked: totalRaceLaps >= 25, progress: p(totalRaceLaps, 25) },
    { key: "laps_50", label: "Half Century", desc: "Complete 50 race laps", unlocked: totalRaceLaps >= 50, progress: p(totalRaceLaps, 50) },
    { key: "laps_75", label: "Getting Comfy", desc: "Complete 75 race laps", unlocked: totalRaceLaps >= 75, progress: p(totalRaceLaps, 75) },
    { key: "laps_100", label: "Century Club", desc: "Complete 100 race laps", unlocked: totalRaceLaps >= 100, progress: p(totalRaceLaps, 100) },
    { key: "laps_150", label: "Long Haul", desc: "Complete 150 race laps", unlocked: totalRaceLaps >= 150, progress: p(totalRaceLaps, 150) },
    { key: "laps_200", label: "Two Hundred", desc: "Complete 200 race laps", unlocked: totalRaceLaps >= 200, progress: p(totalRaceLaps, 200) },
    { key: "laps_300", label: "Triple Digits+", desc: "Complete 300 race laps", unlocked: totalRaceLaps >= 300, progress: p(totalRaceLaps, 300) },
    { key: "laps_400", label: "Mileage", desc: "Complete 400 race laps", unlocked: totalRaceLaps >= 400, progress: p(totalRaceLaps, 400) },
    { key: "laps_500", label: "Endurer", desc: "Complete 500 race laps", unlocked: totalRaceLaps >= 500, progress: p(totalRaceLaps, 500) },
    { key: "laps_750", label: "Iron Mileage", desc: "Complete 750 race laps", unlocked: totalRaceLaps >= 750, progress: p(totalRaceLaps, 750) },
    { key: "laps_1000", label: "Four Digits", desc: "Complete 1000 race laps", unlocked: totalRaceLaps >= 1000, progress: p(totalRaceLaps, 1000) },
    { key: "laps_1250", label: "Track Resident", desc: "Complete 1250 race laps", unlocked: totalRaceLaps >= 1250, progress: p(totalRaceLaps, 1250) },
    { key: "laps_1500", label: "No Days Off", desc: "Complete 1500 race laps", unlocked: totalRaceLaps >= 1500, progress: p(totalRaceLaps, 1500) },
    { key: "laps_2000", label: "Lifetime Driver", desc: "Complete 2000 race laps", unlocked: totalRaceLaps >= 2000, progress: p(totalRaceLaps, 2000) },

    // =====================
    // EXPLORATION: UNIQUE TRACKS (15)
    // =====================
    { key: "tracks_2", label: "Two-Step", desc: "Race on 2 different tracks", unlocked: uniqueTracks >= 2, progress: p(uniqueTracks, 2) },
    { key: "tracks_5", label: "Explorer", desc: "Race on 5 different tracks", unlocked: uniqueTracks >= 5, progress: p(uniqueTracks, 5) },
    { key: "tracks_10", label: "World Tour", desc: "Race on 10 different tracks", unlocked: uniqueTracks >= 10, progress: p(uniqueTracks, 10) },
    { key: "tracks_20", label: "Atlas", desc: "Race on 20 different tracks", unlocked: uniqueTracks >= 20, progress: p(uniqueTracks, 20) },
    { key: "tracks_30", label: "Everywhere", desc: "Race on 30 different tracks", unlocked: uniqueTracks >= 30, progress: p(uniqueTracks, 30) },

    // =====================
    // SIM LOYALTY: AC RACES (10)
    // =====================
    { key: "ac_races_1", label: "AC Debut", desc: "Finish 1 race in Assetto Corsa", unlocked: racesAC >= 1, progress: p(racesAC, 1) },
    { key: "ac_races_5", label: "AC Settled In", desc: "Finish 5 races in Assetto Corsa", unlocked: racesAC >= 5, progress: p(racesAC, 5) },
    { key: "ac_races_10", label: "AC Veteran", desc: "Finish 10 races in Assetto Corsa", unlocked: racesAC >= 10, progress: p(racesAC, 10) },
    { key: "ac_races_20", label: "AC Mainstay", desc: "Finish 20 races in Assetto Corsa", unlocked: racesAC >= 20, progress: p(racesAC, 20) },
    { key: "ac_races_40", label: "AC Grinder", desc: "Finish 40 races in Assetto Corsa", unlocked: racesAC >= 40, progress: p(racesAC, 40) },
    { key: "ac_races_75", label: "AC Mythic", desc: "Finish 75 races in Assetto Corsa", unlocked: racesAC >= 75, progress: p(racesAC, 75) },

    // =====================
    // SIM LOYALTY: ACC RACES (10)
    // =====================
    { key: "acc_races_1", label: "ACC Debut", desc: "Finish 1 race in ACC", unlocked: racesACC >= 1, progress: p(racesACC, 1) },
    { key: "acc_races_5", label: "ACC Settled In", desc: "Finish 5 races in ACC", unlocked: racesACC >= 5, progress: p(racesACC, 5) },
    { key: "acc_races_15", label: "ACC Diehard", desc: "Finish 15 races in ACC", unlocked: racesACC >= 15, progress: p(racesACC, 15) },
    { key: "acc_races_30", label: "ACC Grinder", desc: "Finish 30 races in ACC", unlocked: racesACC >= 30, progress: p(racesACC, 30) },
    { key: "acc_races_75", label: "ACC Mythic", desc: "Finish 75 races in ACC", unlocked: racesACC >= 75, progress: p(racesACC, 75) },

    // Assetto Corsa EVO progression
    { key: "ace_races_1", label: "Evolution Begins", desc: "Finish 1 race in Assetto Corsa EVO", unlocked: racesACE >= 1, progress: p(racesACE, 1) },
    { key: "ace_races_5", label: "EVO Regular", desc: "Finish 5 races in Assetto Corsa EVO", unlocked: racesACE >= 5, progress: p(racesACE, 5) },
    { key: "ace_races_10", label: "Next Generation", desc: "Finish 10 races in Assetto Corsa EVO", unlocked: racesACE >= 10, progress: p(racesACE, 10) },
    { key: "ace_races_25", label: "EVO Veteran", desc: "Finish 25 races in Assetto Corsa EVO", unlocked: racesACE >= 25, progress: p(racesACE, 25) },
    { key: "ace_races_50", label: "Fully Evolved", desc: "Finish 50 races in Assetto Corsa EVO", unlocked: racesACE >= 50, progress: p(racesACE, 50) },
    { key: "all_sims_1", label: "Triple Threat", desc: "Finish a race in AC, ACC, and Assetto Corsa EVO", unlocked: racesAC >= 1 && racesACC >= 1 && racesACE >= 1 },

    // =====================
    // SIM VERSATILITY (5)
    // =====================
    { key: "both_sims_1_1", label: "Dual Citizen", desc: "Finish at least 1 race in AC and 1 in ACC", unlocked: racesAC >= 1 && racesACC >= 1 },
    { key: "both_sims_5_5", label: "Cross-Trained", desc: "Finish 5 races in AC and 5 in ACC", unlocked: racesAC >= 5 && racesACC >= 5 },
    { key: "both_sims_10_10", label: "Two Worlds", desc: "Finish 10 races in AC and 10 in ACC", unlocked: racesAC >= 10 && racesACC >= 10 },
    { key: "both_sims_25_25", label: "Versatile", desc: "Finish 25 races in AC and 25 in ACC", unlocked: racesAC >= 25 && racesACC >= 25 },
    { key: "both_sims_50_50", label: "Master of Both", desc: "Finish 50 races in AC and 50 in ACC", unlocked: racesAC >= 50 && racesACC >= 50 },

    // =====================
    // PLACEMENTS: TOP 15 (5)
    // =====================
    { key: "top15_1", label: "In the Mix", desc: "Score 1 top-15 finish", unlocked: top15s >= 1, progress: p(top15s, 1) },
    { key: "top15_10", label: "Dependable", desc: "Score 10 top-15 finishes", unlocked: top15s >= 10, progress: p(top15s, 10) },
    { key: "top15_35", label: "Workhorse", desc: "Score 35 top-15 finishes", unlocked: top15s >= 35, progress: p(top15s, 35) },

    // =====================
    // PLACEMENTS: TOP 10 (10)
    // =====================
    { key: "top10_1", label: "Points Taste", desc: "Score 1 top-10 finish", unlocked: top10s >= 1, progress: p(top10s, 1) },
    { key: "top10_5", label: "Points Regular", desc: "Score 5 top-10 finishes", unlocked: top10s >= 5, progress: p(top10s, 5) },
    { key: "top10_10", label: "Top Ten Club", desc: "Score 10 top-10 finishes", unlocked: top10s >= 10, progress: p(top10s, 10) },
    { key: "top10_30", label: "Top Ten Machine", desc: "Score 30 top-10 finishes", unlocked: top10s >= 30, progress: p(top10s, 30) },
    { key: "top10_60", label: "Unreal Consistency", desc: "Score 60 top-10 finishes", unlocked: top10s >= 60, progress: p(top10s, 60) },

    // =====================
    // PLACEMENTS: TOP 5 (10)
    // =====================
    { key: "top5_1", label: "Knocking", desc: "Score 1 top-5 finish", unlocked: top5s >= 1, progress: p(top5s, 1) },
    { key: "top5_5", label: "Top Five Habit", desc: "Score 5 top-5 finishes", unlocked: top5s >= 5, progress: p(top5s, 5) },
    { key: "top5_10", label: "Elite Regular", desc: "Score 10 top-5 finishes", unlocked: top5s >= 10, progress: p(top5s, 10) },
    { key: "top5_25", label: "Top Five Engine", desc: "Score 25 top-5 finishes", unlocked: top5s >= 25, progress: p(top5s, 25) },
    { key: "top5_50", label: "Untouchable Pace", desc: "Score 50 top-5 finishes", unlocked: top5s >= 50, progress: p(top5s, 50) },

    // =====================
    // PLACEMENTS: TOP 2 (10)
    // =====================
    { key: "top2_1", label: "Close to Gold", desc: "Score 1 top-2 finish", unlocked: top2s >= 1, progress: p(top2s, 1) },
    { key: "top2_5", label: "Always There", desc: "Score 5 top-2 finishes", unlocked: top2s >= 5, progress: p(top2s, 5) },
    { key: "top2_10", label: "Front Two Club", desc: "Score 10 top-2 finishes", unlocked: top2s >= 10, progress: p(top2s, 10) },
    { key: "top2_30", label: "Title Contender", desc: "Score 30 top-2 finishes", unlocked: top2s >= 30, progress: p(top2s, 30) },
    { key: "top2_50", label: "Front Two Machine", desc: "Score 50 top-2 finishes", unlocked: top2s >= 50, progress: p(top2s, 50) },

    // =====================
    // STREAKS: TOP 10 (5)
    // =====================
    { key: "top10_streak_2", label: "Good Run", desc: "Best top-10 streak of 2", unlocked: bestTop10Streak >= 2, progress: p(bestTop10Streak, 2) },
    { key: "top10_streak_5", label: "Rolling", desc: "Best top-10 streak of 5", unlocked: bestTop10Streak >= 5, progress: p(bestTop10Streak, 5) },
    { key: "top10_streak_10", label: "Unstoppable", desc: "Best top-10 streak of 10", unlocked: bestTop10Streak >= 10, progress: p(bestTop10Streak, 10) },

    // =====================
    // STREAKS: TOP 5 (5)
    // =====================
    { key: "top5_streak_2", label: "Front Pack Run", desc: "Best top-5 streak of 2", unlocked: bestTop5Streak >= 2, progress: p(bestTop5Streak, 2) },
    { key: "top5_streak_5", label: "Front Pack Run", desc: "Best top-5 streak of 5", unlocked: bestTop5Streak >= 5, progress: p(bestTop5Streak, 5) },
    { key: "top5_streak_10", label: "Pure Class", desc: "Best top-5 streak of 10", unlocked: bestTop5Streak >= 10, progress: p(bestTop5Streak, 10) },

    // =====================
    // TRACK SPECIALIST: MOST RACES ON ONE TRACK (5)
    // =====================
    { key: "track_specialist_3", label: "Local", desc: "Race the same track 3 times", unlocked: mostRacesOnOneTrack >= 3, progress: p(mostRacesOnOneTrack, 3) },
    { key: "track_specialist_10", label: "Specialist", desc: "Race the same track 10 times", unlocked: mostRacesOnOneTrack >= 10, progress: p(mostRacesOnOneTrack, 10) },
    { key: "track_specialist_20", label: "Track Master", desc: "Race the same track 20 times", unlocked: mostRacesOnOneTrack >= 20, progress: p(mostRacesOnOneTrack, 20) },

    // =====================
    // POSITION QUALITY (5) — no progress (threshold style like your bestPos ones)
    // =====================
    { key: "worst_pos_20_or_better", label: "Never Too Far", desc: "Worst finish is P20 or better", unlocked: Number.isFinite(worstPos) && worstPos <= 20 },
    { key: "worst_pos_12_or_better", label: "No Bad Days", desc: "Worst finish is P12 or better", unlocked: Number.isFinite(worstPos) && worstPos <= 12 },
    { key: "avg_pos_10_or_better", label: "Strong Average", desc: "Average finish position ≤ 10", unlocked: Number.isFinite(avgPos) && avgPos <= 10 },
  ];
}

const Badge = ({ unlocked, title, desc, progress, rarityPct, rarityCount, totalDrivers }) => {
  const need = Number(progress?.need) || 0;
  const have = Number(progress?.have) || 0;

  const pctRaw = need > 0 ? (have / need) * 100 : 0;
  const pct = unlocked ? 100 : Math.max(0, Math.min(100, pctRaw));

  const rarityLabel =
    Number.isFinite(rarityPct) && Number.isFinite(rarityCount) && Number.isFinite(totalDrivers)
      ? `${rarityPct}% drivers`
      : null;

  const right = (
    <Stack direction="column" spacing={0.75} alignItems="flex-end">
      {!unlocked && need > 0 ? (
        <Chip
          size="small"
          label={`${Math.min(have, need)}/${need}`}
          sx={{ fontWeight: 700 }}
          variant="outlined"
        />
      ) : unlocked ? (
        <Chip size="small" color="success" label="Unlocked" sx={{ fontWeight: 700 }} />
      ) : null}

      {rarityLabel ? (
        <Tooltip title={`${rarityCount}/${totalDrivers} drivers have this achievement`}>
          <Chip
            size="small"
            label={rarityLabel}
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
        </Tooltip>
      ) : null}
    </Stack>
  );

  return (
    <Paper
      elevation={unlocked ? 2 : 0}
      sx={{
        p: 1.5,
        borderRadius: 2,
        position: "relative",
        overflow: "hidden",
        bgcolor: unlocked ? "background.paper" : "action.hover",
        opacity: unlocked ? 1 : 0.92,
        border: (theme) =>
          `1px dashed ${unlocked ? theme.palette.success.light : theme.palette.divider}`,
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          width: `${pct}%`,
          background: (theme) => {
            const a = unlocked ? 0.22 : 0.16;
            const c1 = theme.palette.primary.main;
            const c2 = theme.palette.success.main;
            const c3 = theme.palette.warning.main;
            return `linear-gradient(90deg,
              ${c1}${Math.round(a * 255).toString(16).padStart(2, "0")} 0%,
              ${c2}${Math.round(a * 255).toString(16).padStart(2, "0")} 55%,
              ${c3}${Math.round((a * 0.9) * 255).toString(16).padStart(2, "0")} 100%
            )`;
          },
          transition: "width 420ms ease",
          zIndex: 0,
        },
        "&::after": unlocked
          ? undefined
          : {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(110deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.10) 40%, rgba(255,255,255,0) 70%)",
            transform: "translateX(-60%)",
            animation: need > 0 ? "badgeSheen 1.8s ease-in-out infinite" : "none",
            zIndex: 0,
          },
        "& .BadgeContent": { position: "relative", zIndex: 1 },
        "@keyframes badgeSheen": {
          "0%": { transform: "translateX(-60%)" },
          "100%": { transform: "translateX(60%)" },
        },
      }}
    >
      <Box className="BadgeContent">
        <List disablePadding sx={{ width: "100%" }}>
          <ListItem
            disableGutters
            secondaryAction={right}
            sx={{
              pr: 12,
              alignItems: "center"
            }}
          >
            <ListItemAvatar>
              <Avatar sx={{ width: 36, height: 36 }}>
                {unlocked ? <EmojiEventsIcon /> : <LockOutlinedIcon />}
              </Avatar>
            </ListItemAvatar>

            <ListItemText
              sx={{ mr: 2}}
              primary={<Typography sx={{ fontWeight: 800 }}>{title}</Typography>}
              secondary={
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", width: {sm:"1000px", lg:"149px"}, height: "34px" }}>
                  {desc}
                </Typography>
              }
            />
          </ListItem>
        </List>
      </Box>
    </Paper>
  );
};

/* =========================
   NEW: Mobile session row (low noise)
   ========================= */

function SessionRowCard({ r, lapMax, onClick }) {
  const laps = Number.isFinite(r?.laps) ? r.laps : null;
  const pct = laps ? Math.max(4, Math.round((laps / lapMax) * 100)) : 0;

  // Keep mobile calm:
  // - 1 headline line: Track + chips
  // - 1 sub line: Best lap + position
  // - optional quiet lap bar
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        px: 1.5,
        py: 1.25,
        "&:active": { opacity: 0.85 },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }} flexWrap="wrap">
            <Typography sx={{ fontWeight: 900 }} noWrap>
              {r.track}
            </Typography>
            <SimChip sim={r.sim} />
            <SessionChip session={r.session} />
          </Stack>

          <Typography variant="body2" color="text.secondary" noWrap>
            Best {msToLap(r.best)}{" "}
            {Number.isFinite(r?.position) ? `• P${r.position}` : ""}{" "}
            {laps ? `• ${laps} laps` : ""}
          </Typography>

          {laps ? (
            <Box sx={{ mt: 0.75, display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: 8,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${pct}%`,
                    bgcolor: "primary.main",
                    opacity: 0.65,
                  }}
                />
              </Box>
            </Box>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}

/* =========================
   MAIN COMPONENT
   ========================= */

export default function DriverProfilePage({ data }) {
  const { id } = useParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [allDriverRowsMap, setAllDriverRowsMap] = useState({});
  const [sessionFilter, setSessionFilter] = useState("all"); // all | Practice | Qualifying | Race

  const navigate = useNavigate();

  // driver lookup (by id or number)
  const driver = useMemo(() => {
    const list = data?.drivers?.list ?? [];
    return list.find((d) => `${d.id}` === id || `${d.number}` === id) || null;
  }, [data, id]);
  const steamProfileId = useMemo(() => cleanId(driver?.id), [driver?.id]);
  const steamProfileUrl = steamProfileId
    ? `https://steamcommunity.com/profiles/${steamProfileId}/`
    : "";

  // Fetch session JSON for both sims (now cached)
  useEffect(() => {
    if (!driver?.id) return;
    let alive = true;

    (async () => {
      setLoading(true);

      const toUrl = (f) =>
        process.env.PUBLIC_URL
          ? `${process.env.PUBLIC_URL}${f.startsWith("/") ? "" : "/"}${f}`
          : `${f.startsWith("/") ? "" : "/"}${f}`;

      const getACCDateString = (f) => {
        let full_date_list = f.split("/")[1].split("_");
        let full_date_string = full_date_list[0] + full_date_list[1];
        return full_date_string;
      }

      const getACDateString = (f) => {
        let full_date_list = f.split("/")[1];
        full_date_list = full_date_list.split(".");
        full_date_list = full_date_list[0];
        full_date_list = full_date_list.split("_");

        let full_date_string = full_date_list[0] + full_date_list[1] + full_date_list[2] + full_date_list[3] + full_date_list[4];
        return full_date_string;
      }

      const [accList, acList, aceList] = await Promise.all([
        Promise.all(ACC_FILES.sort((a, b) => getACCDateString(a) - getACCDateString(b)).map((f) => fetchJSON(toUrl(f)))),
        Promise.all(AC_FILES.sort((a, b) => getACDateString(a) - getACDateString(b)).map((f) => fetchJSON(toUrl(f)))),
        Promise.all(AC_EVO_FILES.map((f) => fetchJSON(toUrl(f)))),
      ]);
      const accJsons = accList.filter(Boolean);
      const acJsons = acList.filter(Boolean);
      const aceJsons = aceList.filter(Boolean);

      const currentAcc = accJsons.map((j, i) => j && parseACC(j, driver.id, ACC_FILES[i])).filter(Boolean);
      const currentAc = acJsons.map((j, i) => j && parseAC(j, driver.id, AC_FILES[i])).filter(Boolean);
      const currentAce = aceJsons.map((j, i) => aceSessionRow(j, driver.id, AC_EVO_FILES[i])).filter(Boolean);

      const driversList = data?.drivers?.list ?? [];
      const nextAllDriverRowsMap = {};

      for (const d of driversList) {
        const dAcc = accJsons.map((j, i) => parseACC(j, d.id, ACC_FILES[i])).filter(Boolean);
        const dAc = acJsons.map((j, i) => parseAC(j, d.id, AC_FILES[i])).filter(Boolean);
        const dAce = aceJsons.map((j, i) => aceSessionRow(j, d.id, AC_EVO_FILES[i])).filter(Boolean);
        nextAllDriverRowsMap[String(d.id)] = [...dAcc, ...dAc, ...dAce].reverse();
      }

      if (!alive) return;

      setRows([...currentAc, ...currentAcc, ...currentAce].reverse());
      setAllDriverRowsMap(nextAllDriverRowsMap);
      setLoading(false);
    })();

    return () => {
      alive = false;
    };
  }, [driver?.id, data]);

  // Derived aggregates
  const stats = useMemo(() => {
    const bySim = { AC: [], ACC: [], AC_EVO: [] };
    for (const r of rows) {
      if (r?.sim === "AC") bySim.AC.push(r);
      else if (r?.sim === "ACC") bySim.ACC.push(r);
      else if (r?.sim === "AC_EVO") bySim.AC_EVO.push(r);
    }

    const sumLaps = (arr) =>
      arr
        .filter((r) => r.session == "Race")
        .reduce((acc, r) => (Number.isFinite(r?.laps) ? acc + r.laps : acc), 0);

    const bestOf = (arr) => {
      let best = null;
      let meta = null;
      const filtered_arr = arr.filter((r) => r.session == "Race");
      for (const r of filtered_arr) {
        if (
          Number.isFinite(r?.best) &&
          r.best > 0 &&
          r.best !== 999999999 &&
          r.best !== 2147483647
        ) {
          if (best === null || r.best < best) {
            best = r.best;
            meta = r;
          }
        }
      }
      return best ? { time: best, meta } : null;
    };

    const all = rows;
    return {
      sessionsTotal: all.filter((r) => r.session == "Race").length,
      sessionsAC: bySim.AC.filter((r) => r.session == "Race").length,
      sessionsACC: bySim.ACC.filter((r) => r.session == "Race").length,
      sessionsACE: bySim.AC_EVO.filter((r) => r.session == "Race").length,
      lapsTotal: sumLaps(all),
      lapsAC: sumLaps(bySim.AC),
      lapsACC: sumLaps(bySim.ACC),
      lapsACE: sumLaps(bySim.AC_EVO),
      pbAC: bestOf(bySim.AC),
      pbACC: bestOf(bySim.ACC),
      pbACE: bestOf(bySim.AC_EVO),
      pbAll: bestOf(all),
    };
  }, [rows]);

  // Race-only tallies for License & Achievements
  const raceRows = useMemo(
    () => rows.filter((r) => (r.session || "").toLowerCase() === "race"),
    [rows]
  );

  const raceStats = useMemo(() => computeRaceStatsFromRows(rows), [rows]);

  const licenseInfo = useMemo(() => scoreToLicense(raceStats.X || 0), [raceStats.X]);

  const achievementsAll = useMemo(() => buildAchievements(raceStats), [raceStats]);
  const [unlockedAchievements, lockedAchievements] = useMemo(() => {
    const unlocked = achievementsAll.filter((a) => a.unlocked);
    const locked = achievementsAll.filter((a) => !a.unlocked);
    return [unlocked, locked];
  }, [achievementsAll]);
  const achievements = useMemo(
    () => [...unlockedAchievements, ...lockedAchievements],
    [unlockedAchievements, lockedAchievements]
  );

  const achievementOwnership = useMemo(() => {
    const driversList = data?.drivers?.list ?? [];
    const totalDrivers = driversList.length;

    if (!totalDrivers) {
      return { totalDrivers: 0, byKey: {} };
    }

    const counts = {};

    for (const d of driversList) {
      const dRows = allDriverRowsMap[String(d.id)] || [];
      const dRaceStats = computeRaceStatsFromRows(dRows);
      const dAchievements = buildAchievements(dRaceStats);

      for (const a of dAchievements) {
        if (a.unlocked) {
          counts[a.key] = (counts[a.key] || 0) + 1;
        }
      }
    }

    const byKey = {};
    for (const a of achievementsAll) {
      const count = counts[a.key] || 0;
      byKey[a.key] = {
        count,
        pct: totalDrivers > 0 ? Math.round((count / totalDrivers) * 100) : 0,
      };
    }

    return { totalDrivers, byKey };
  }, [data, allDriverRowsMap, achievementsAll]);

  // Filters
  const filteredRows = useMemo(() => {
    let list = rows;
    if (tab === "ac") list = list.filter((r) => r.sim === "AC");
    if (tab === "acc") list = list.filter((r) => r.sim === "ACC");
    if (tab === "ace") list = list.filter((r) => r.sim === "AC_EVO");
    if (sessionFilter !== "all")
      list = list.filter(
        (r) => (r.session || "").toLowerCase() === sessionFilter.toLowerCase()
      );
    return list;
  }, [rows, tab, sessionFilter]);

  const lapMax = useMemo(() => {
    let m = 0;
    for (const r of filteredRows) if (Number.isFinite(r?.laps)) m = Math.max(m, r.laps);
    return Math.max(m, 1);
  }, [filteredRows]);

  const handleGoBack = () => window.history.back();

  if (!driver) {
    return (
      <PageShell title="Driver" bannerImage="/f1.png">
        <Container sx={{ py: 4 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Driver not found
          </Typography>
          <Button onClick={handleGoBack} variant="contained">
            Back
          </Button>
        </Container>
      </PageShell>
    );
  }

  return (
    <PageShell title={driver.name} bannerImage="/f1.png">
      <Container maxWidth="xl" sx={{ py: 1 }}>
        {/* Hero */}
        <Paper
          sx={{
            p: 1.5,
            borderRadius: 1,
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.02), rgba(255,255,255,0.06))",
            backdropFilter: "blur(2px)",
          }}
        >
          <Grid container spacing={1} alignItems="center">
            <Grid item xs="auto" sx={{ margin: "auto" }}>
              <Avatar src={driver.image} sx={{ width: 400, height: 400, maxWidth: "100%", borderRadius: 2 }} />
            </Grid>
            <Grid item xs>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h4" sx={{ fontWeight: 900 }}>
                    {driver.name}
                  </Typography>
                  <Chip label={`#${driver.number}`} />
                  {driver.license && <Chip color="white" label={getLicenseLabel(computeLicensePoints(driver))} sx={{backgroundColor: getLicenseColor(getLicenseLabel(computeLicensePoints(driver)))}}/>}
                  {/* {steamProfileUrl && (
                    <Link
                      href={steamProfileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      sx={{ display: "inline-flex" }}
                    >
                      <Chip
                        icon={<SteamIcon />}
                        label="Steam"
                        clickable
                        variant="outlined"
                        sx={{ fontWeight: 700 }}
                      />
                    </Link>
                  )} */}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" onClick={() => navigate(`/team/${driver.team}`)} sx={{ cursor: "pointer" }}>
                  <img src={getTeamImage(driver.team)} onError={useTeamImageFallback} alt={driver.team} height={100} style={{ borderRadius: "2px" }} />
                  <Chip size="small" label={driver.team} />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip size="small" label={driver.car} />
                </Stack>

                {/* License strip */}
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip
                      label={`${licenseInfo.tier.name} License`}
                      sx={{
                        bgcolor: "transparent",
                        border: `2px solid ${licenseInfo.tier.color}`,
                        color: licenseInfo.tier.color,
                        fontWeight: 900,
                        m: 1,
                      }}
                      size="small"
                    />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Tooltip title="License Points = (Finishes × 5) + (Podiums × 5) + (Wins × 5)">
                      <Chip label={`License Points = ${raceStats.X ?? 0}`} size="small" sx={{m:1}} />
                    </Tooltip>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Fin ${raceStats.finishes ?? 0} · Pod ${raceStats.podiums ?? 0} · Wins ${raceStats.wins ?? 0
                        }`}
                      sx={{m:1}}
                    />
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={licenseInfo.progressPct}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: "action.hover",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 5,
                        bgcolor: licenseInfo.tier.color,
                      },
                      m: 1,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {licenseInfo.next
                      ? `Progress to ${licenseInfo.next.name} (${licenseInfo.tier.min}–${licenseInfo.tier.max})`
                      : `Top tier achieved`}
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Divider sx={{ my: 3 }} />

        {/* Headline stats (leave as-is; already clean on mobile due to grid stacking) */}
        <Grid container spacing={3}>
          <Grid item sx={{width: {sm: "100%", lg: "31%"}}}>
            <Paper sx={{ p: 2, borderRadius: 3, background: "linear-gradient(135deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.06))" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                Overall
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <StatBlock label="Sessions" value={stats.sessionsTotal} sub={`${stats.lapsTotal} laps`} />
                <StatBlock
                  label="PB (All)"
                  value={stats.pbAll ? msToLap(stats.pbAll.time) : "—"}
                  sub={stats.pbAll?.meta?.track || "—"}
                />
              </Stack>
            </Paper>
          </Grid>
          <Grid item sx={{width: {sm: "100%", lg: "31%"}}}>
            <Paper sx={{ p: 2, borderRadius: 3, background: "linear-gradient(135deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.06))" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                Assetto Corsa EVO
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <StatBlock label="Sessions" value={stats.sessionsACE} sub={`${stats.lapsACE} laps`} />
                <StatBlock
                  label="PB (ACE)"
                  value={stats.pbACE ? msToLap(stats.pbACE.time) : "—"}
                  sub={stats.pbACE?.meta?.track || "—"}
                />
              </Stack>
            </Paper>
          </Grid>
          <Grid item sx={{width: {xs: "100dvw", lg: "31%"}}}>
            <Paper sx={{ p: 2, borderRadius: 3, background: "linear-gradient(135deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.06))" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                Assetto Corsa
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <StatBlock label="Sessions" value={stats.sessionsAC} sub={`${stats.lapsAC} laps`} />
                <StatBlock
                  label="PB (AC)"
                  value={stats.pbAC ? msToLap(stats.pbAC.time) : "—"}
                  sub={stats.pbAC?.meta?.track || "—"}
                  sx={{ ml: "auto" }}
                />
              </Stack>
            </Paper>
          </Grid>
          <Grid item sx={{width: {sm: "100%", lg: "31%"}}}>
            <Paper sx={{ p: 2, borderRadius: 3, background: "linear-gradient(135deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.06))" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                Assetto Corsa Competizione
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <StatBlock label="Sessions" value={stats.sessionsACC} sub={`${stats.lapsACC} laps`} />
                <StatBlock
                  label="PB (ACC)"
                  value={stats.pbACC ? msToLap(stats.pbACC.time) : "—"}
                  sub={stats.pbACC?.meta?.track || "—"}
                />
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* Tabs + Session Filter */}
        <Paper sx={{ p: 2, borderRadius: 3, mt: 3, background: "linear-gradient(135deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.06))" }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 1 }}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 36,
                "& .MuiTab-root": { minHeight: 36, fontWeight: 700 },
              }}
            >
              <Tab value="all" label={`All (${stats.sessionsTotal})`} />
              <Tab value="acc" label={`ACC (${stats.sessionsACC})`} />
              <Tab value="ace" label={`ACE (${stats.sessionsACE})`} />
              <Tab value="ac" label={`AC (${stats.sessionsAC})`} />
            </Tabs>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              {["all", "Practice", "Qualifying", "Race"].map((s) => {
                const active = sessionFilter === s;
                return (
                  <Chip
                    key={s}
                    label={s}
                    onClick={() => setSessionFilter(s)}
                    color={active ? "primary" : "default"}
                    variant={active ? "filled" : "outlined"}
                    size="small"
                    sx={{ borderRadius: 2 }}
                  />
                );
              })}
            </Stack>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {filteredRows.length} sessions •{" "}
            {filteredRows.reduce((a, r) => a + (Number.isFinite(r?.laps) ? r.laps : 0), 0)} laps shown
          </Typography>

          {/* MOBILE: low-noise list */}
          <Box sx={{ display: { xs: "block", md: "none" }, mt: 1.5 }}>
            <Paper sx={{ borderRadius: 1, overflow: "hidden" }}>
              {loading ? (
                <Box sx={{ p: 1.5 }}>
                  {[...Array(6)].map((_, i) => (
                    <Box key={`msk-${i}`} sx={{ mb: 1.25 }}>
                      <Skeleton width="60%" />
                      <Skeleton width="85%" />
                    </Box>
                  ))}
                </Box>
              ) : filteredRows.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No sessions match the current filters.
                  </Typography>
                </Box>
              ) : (
                filteredRows.map((r, i) => (
                  <Box key={`mrow-${i}`}>
                    <SessionRowCard r={r} lapMax={lapMax} onClick={() => navigate(r.fileName ? `/results?session=${encodeURIComponent(r.fileName)}` : "/results")} />
                    {i !== filteredRows.length - 1 && <Divider />}
                  </Box>
                ))
              )}
            </Paper>
          </Box>

          {/* DESKTOP: keep your table */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <TableContainer sx={{ mt: 1.5, maxHeight: 420, borderRadius: 2 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Sim</TableCell>
                    <TableCell>Track</TableCell>
                    <TableCell>Session</TableCell>
                    <TableCell>Best Lap</TableCell>
                    <TableCell align="right">Position</TableCell>
                    <TableCell width={220}>Lap Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && (
                    <>
                      {[...Array(5)].map((_, i) => (
                        <TableRow key={`sk-${i}`}>
                          <TableCell>
                            <Skeleton width={28} />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={120} />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={90} />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={80} />
                          </TableCell>
                          <TableCell align="right">
                            <Skeleton width={36} />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={180} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}

                  {!loading && filteredRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} style={{ paddingTop: 24, paddingBottom: 24 }}>
                        <Typography variant="body2" color="text.secondary">
                          No sessions match the current filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {!loading &&
                    filteredRows.map((r, i) => {
                      const laps = Number.isFinite(r?.laps) ? r.laps : null;
                      const pct = laps ? Math.max(4, Math.round((laps / lapMax) * 100)) : 0;
                      return (
                        <TableRow key={i} hover onClick={() => navigate(r.fileName ? `/results?session=${encodeURIComponent(r.fileName)}` : "/results")}>
                          <TableCell>
                            <SimChip sim={r.sim} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {r.track}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <SessionChip session={r.session} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{msToLap(r.best)}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{color: r.position === 1 ? getLicenseColor("gold") : r.position === 2 ? getLicenseColor("silver") : r.position === 3 ? getLicenseColor("bronze") : "text.primary"}}>
                            {Number.isFinite(r?.position) ? r.position : "—"}
                          </TableCell>
                          <TableCell>
                            {laps ? (
                              <Tooltip title={`${laps} laps`}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Box
                                    sx={{
                                      position: "relative",
                                      width: "100%",
                                      height: 10,
                                      bgcolor: "action.hover",
                                      borderRadius: 1,
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: `${pct}%`,
                                        bgcolor: "primary.main",
                                        opacity: 0.7,
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ minWidth: 24, textAlign: "right" }}
                                  >
                                    {laps}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>

        {/* Achievements (already pretty clean on mobile because each card is one per row) */}
        <Typography variant="h6" sx={{ fontWeight: 900, mt: 3, mb: 1 }}>
          Achievements
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {unlockedAchievements.length} unlocked · {achievements.length} total
        </Typography>

        {unlockedAchievements.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, opacity: 0.9 }}>
              Unlocked
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {unlockedAchievements.sort((a, b) => achievementOwnership.byKey[a.key]?.pct - achievementOwnership.byKey[b.key]?.pct).map((a) => (
                <Grid key={`u-${a.key}`} size={{ xs: 12, md: 6 }}>
                  <Badge
                    unlocked
                    title={a.label}
                    desc={a.desc}
                    progress={a.progress}
                    rarityPct={achievementOwnership.byKey[a.key]?.pct}
                    rarityCount={achievementOwnership.byKey[a.key]?.count}
                    totalDrivers={achievementOwnership.totalDrivers}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {lockedAchievements.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, opacity: 0.9 }}>
              Locked
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {lockedAchievements
                .sort((b, a) => (a.progress?.have / a.progress?.need - b.progress?.have / b.progress?.need))
                .map((a) => (
                  <Grid key={`l-${a.key}`} size={{ xs: 12, md: 6 }}>
                    <Badge
                      unlocked={false}
                      title={a.label}
                      desc={a.desc}
                      progress={a.progress}
                      rarityPct={achievementOwnership.byKey[a.key]?.pct}
                      rarityCount={achievementOwnership.byKey[a.key]?.count}
                      totalDrivers={achievementOwnership.totalDrivers}
                    />
                  </Grid>
                ))}
            </Grid>
          </>
        )}

        <Stack direction="row" sx={{ mt: 3 }}>
          <Button onClick={handleGoBack} variant="contained" startIcon={<CheckCircleIcon />}>
            Back
          </Button>
        </Stack>
      </Container>
    </PageShell>
  );
}
